import React from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Button,
  CircularProgress,
} from '@mui/material';
import { Lock as LockIcon, Save as SaveIcon } from '@mui/icons-material';
import { colors, fonts, spacing, borderRadius, shadows } from '../../constants';
import { PRIMARY_BUTTON_STYLES, MIN_PASSWORD_LENGTH } from '../../constants/settingsPage';
import SectionHeader from './SectionHeader';
import PasswordField from './PasswordField';

/**
 * PasswordSection Component
 * Password Update card component
 */
const PasswordSection = ({
  passwordData,
  showPasswords,
  isChangingPassword,
  onPasswordChange,
  onToggleVisibility,
  onChangePassword,
  onClear,
}) => {
  // Validation: Check if new password has at least 6 characters
  // Only show error if user has typed something (not empty)
  const newPasswordError = passwordData.newPassword && passwordData.newPassword.length > 0 && passwordData.newPassword.length < MIN_PASSWORD_LENGTH;
  const newPasswordErrorText = newPasswordError ? `Password must be at least ${MIN_PASSWORD_LENGTH} characters` : null;

  // Check if passwords match (only if both are filled)
  const passwordsMatch = passwordData.newPassword && passwordData.confirmPassword && passwordData.newPassword === passwordData.confirmPassword;
  const passwordsMismatch = passwordData.newPassword && passwordData.confirmPassword && passwordData.newPassword !== passwordData.confirmPassword;
  const confirmPasswordError = passwordsMismatch ? 'Passwords do not match' : null;

  // Button should be enabled only if:
  // 1. All fields are filled
  // 2. New password has at least 6 characters
  // 3. Passwords match
  const isFormValid =
    passwordData.currentPassword &&
    passwordData.newPassword &&
    passwordData.confirmPassword &&
    passwordData.newPassword.length >= MIN_PASSWORD_LENGTH &&
    passwordsMatch;

  return (
    <Card
      sx={{
        mb: spacing.sm,
        boxShadow: shadows.md,
        borderRadius: borderRadius.card,
        border: `1px solid ${colors.borderDefault}`,
        backgroundColor: colors.backgroundWhite,
        overflow: 'visible',
        width: '100%',
        maxWidth: '100%',
      }}
    >
      <CardContent sx={{ p: spacing.sm }}>
        <SectionHeader
          icon={LockIcon}
          title="Update Password"
          description="Change your account password securely"
        />

        <Box sx={{ mb: spacing.sm }}>
          <Typography
            component="div"
            sx={{
              fontSize: fonts.fontSizeMd,
              color: colors.textPrimaryDark,
              fontWeight: fonts.weightSemiBold,
              mb: spacing.sm,
              display: 'flex',
              alignItems: 'center',
              gap: spacing.xs,
            }}
          >
            <Box
              sx={{
                width: 3,
                height: 16,
                background: colors.primaryGradient,
                borderRadius: borderRadius.full,
              }}
            />
            Password Information
          </Typography>
        </Box>

        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)' },
            gap: spacing.md,
            width: '100%',
            '& > *': {
              minWidth: 0,
            },
          }}
        >
          <Box sx={{ gridColumn: { xs: '1', sm: 'span 2' } }}>
            <PasswordField
              label="Current Password"
              value={passwordData.currentPassword}
              onChange={(e) => onPasswordChange('currentPassword', e.target.value)}
              showPassword={showPasswords.current}
              onToggleVisibility={() => onToggleVisibility('current')}
              placeholder="Enter your current password"
            />
          </Box>

          <PasswordField
            label="New Password"
            value={passwordData.newPassword}
            onChange={(e) => onPasswordChange('newPassword', e.target.value)}
            showPassword={showPasswords.new}
            onToggleVisibility={() => onToggleVisibility('new')}
            placeholder="Enter new password"
            helperText={!newPasswordError ? "Must be at least 6 characters" : null}
            error={newPasswordError}
            errorText={newPasswordErrorText}
          />

          <PasswordField
            label="Confirm New Password"
            value={passwordData.confirmPassword}
            onChange={(e) => onPasswordChange('confirmPassword', e.target.value)}
            showPassword={showPasswords.confirm}
            onToggleVisibility={() => onToggleVisibility('confirm')}
            placeholder="Confirm new password"
            helperText={!confirmPasswordError ? "Must match new password" : null}
            error={!!confirmPasswordError}
            errorText={confirmPasswordError}
          />
        </Box>

        <Box
          sx={{
            mt: spacing.md,
            display: 'flex',
            justifyContent: 'flex-end',
            gap: spacing.sm,
          }}
        >
          <Button
            onClick={onClear}
            variant="outlined"
            size="medium"
            disabled={isChangingPassword}
            sx={{
              borderColor: colors.borderHover,
              color: colors.textSecondary,
              textTransform: 'none',
              fontWeight: fonts.weightMedium,
              px: spacing.lg,
              py: spacing.sm,
              borderRadius: borderRadius.md,
              fontSize: fonts.fontSizeSm,
              '&:hover': {
                borderColor: colors.borderHover,
                backgroundColor: colors.backgroundSoft,
              },
            }}
          >
            Clear
          </Button>
          <Button
            startIcon={isChangingPassword ? <CircularProgress size={16} sx={{ color: '#FFFFFF' }} /> : <SaveIcon sx={{ color: '#FFFFFF' }} />}
            onClick={onChangePassword}
            variant="contained"
            size="medium"
            disabled={isChangingPassword || !isFormValid}
            sx={{
              ...PRIMARY_BUTTON_STYLES,
              fontWeight: fonts.weightMedium,
              fontSize: fonts.fontSizeSm,
              px: spacing.lg,
              py: spacing.sm,
              borderRadius: borderRadius.md,
              color: '#FFFFFF', // Pure white text
              backgroundColor: colors.primary, // Ensure solid primary color
              background: colors.primaryGradient, // Or gradient if preferred, but ensuring visibility
              filter: 'brightness(1.1)', // Slightly brighter
              '&:hover': {
                filter: 'brightness(0.95)', // Slightly darker on hover for contrast
                boxShadow: shadows.md,
              },
              '&.Mui-disabled': {
                color: colors.textWhite,
                opacity: 0.7,
              },
            }}
          >
            {isChangingPassword ? 'Updating...' : 'Update Password'}
          </Button>
        </Box>
      </CardContent>
    </Card>
  );
};

export default PasswordSection;
